import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const Liste = ({ items }) => (
  <div className="max-h-72 overflow-y-auto rounded border border-slate-200">
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-slate-50 text-left text-slate-600">
        <tr><th className="px-2 py-1.5">Produit</th><th className="px-2 py-1.5">Référence</th><th className="px-2 py-1.5">Souscripteur</th><th className="px-2 py-1.5 text-right">Réglé</th><th className="px-2 py-1.5 text-right">Solde dû</th></tr>
      </thead>
      <tbody>
        {items.map((i, k) => (
          <tr key={`${i.produit}-${i.ident}`} className="border-t border-slate-100" data-testid={`remise-recus-ligne-${k}`}>
            <td className="px-2 py-1.5 font-semibold">{i.produit === 'rcds' ? 'RCDS' : 'DOS-DOMIUM'}</td>
            <td className="px-2 py-1.5">{i.reference}</td>
            <td className="px-2 py-1.5">{i.nom || '—'}</td>
            <td className="px-2 py-1.5 text-right">{eur(i.paye)} / {eur(i.total)}</td>
            <td className="px-2 py-1.5 text-right font-semibold text-amber-700">{eur(i.reste)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Progression = ({ job }) => (
  <div className="space-y-2 text-sm" data-testid="remise-recus-progression">
    <div className="h-2 overflow-hidden rounded bg-slate-100">
      <div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${job.total ? (job.traites / job.total) * 100 : 0}%` }} />
    </div>
    <p>{job.traites} / {job.total} traité(s) — {job.faits} reçu(s) recréé(s), {job.inchanges || 0} déjà à jour, {job.archives} ancien(s) reçu(s) « acquitté » archivé(s){job.statut === 'termine' ? ' — terminé' : '…'}</p>
    {job.erreurs?.length > 0 && <ul className="list-disc pl-5 text-xs text-rose-700">{job.erreurs.map((e) => <li key={e}>{e}</li>)}</ul>}
  </div>
);

export const RemiseRecusPartiels = () => {
  const { user, getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [job, setJob] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    setData(null); setJob(null);
    axios.get(`${API}/recus/partiels`, { headers: getAuthHeader() }).then((r) => setData(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || 'Liste indisponible'); setData({ items: [] }); });
    return () => clearInterval(timer.current);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (user?.role !== 'super_admin') return null;
  const lancer = async () => {
    if (envoi) return;
    setEnvoi(true);
    try {
      const r = await axios.post(`${API}/recus/partiels/remise-a-jour`, {}, { headers: getAuthHeader() });
      setJob(r.data);
      timer.current = setInterval(async () => {
        const s = await axios.get(`${API}/recus/partiels/remise-a-jour/${r.data.id}`, { headers: getAuthHeader() }).catch(() => null);
        if (!s) return;
        setJob(s.data);
        if (s.data.statut === 'termine') { clearInterval(timer.current); toast.success(`${s.data.faits} reçu(s) remis à jour`); }
      }, 2000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Remise à jour impossible'); } finally { setEnvoi(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="gap-2 shadow-sm" data-testid="remise-recus-partiels-btn">
        <RefreshCcw className="h-4 w-4" /> Remettre à jour les reçus partiels
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="remise-recus-dialog">
          <DialogHeader><DialogTitle>Remettre à jour les reçus partiellement payés</DialogTitle></DialogHeader>
          {!data ? <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-400" /> : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600" data-testid="remise-recus-resume">
                {data.items.length} souscription(s) RCDS / DOS-DOMIUM avec un solde dû ({eur(data.total_reste)} au total). Chaque reçu sera recréé avec le montant réglé et le solde dû ; les anciens reçus « acquittés » seront archivés dans la GED (jamais supprimés). Aucun e-mail n'est envoyé.
              </p>
              {data.items.length > 0 && <Liste items={data.items} />}
              {job ? <Progression job={job} /> : (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setOpen(false)} data-testid="remise-recus-annuler">Annuler</Button>
                  <Button onClick={lancer} disabled={!data.items.length || envoi} className="bg-[#0a3d62] text-white hover:bg-[#0a3d62]/90" data-testid="remise-recus-confirmer">
                    {envoi && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Recréer {data.items.length} reçu(s)
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
